import { useEffect, useState } from "react"

import useLocalStorage from "hooks/useLocalStorage"

const useAudioInputDevices = () => {
  const [audioInputDevices, setAudioInputDevices] = useState<MediaDeviceInfo[]>(
    []
  )
  const [selectedAudioInputDeviceId, setSelectedAudioInputDeviceId] =
    useLocalStorage("audioRoom:audioInputDeviceId", "")
  const [microphoneError, setMicrophoneError] = useState<string>()

  useEffect(() => {
    const loadDevices = async () => {
      const devices = await navigator.mediaDevices.enumerateDevices()

      const audioDevices = devices
        .filter(device => device.kind === "audioinput")
        .sort((first, second) => {
          if (["default", "communications"].includes(first.deviceId)) return 1
          if (["default", "communications"].includes(second.deviceId)) return -1
          return 0
        })
      setAudioInputDevices(audioDevices)

      setSelectedAudioInputDeviceId(currentDeviceId =>
        audioDevices.some(device => device.deviceId === currentDeviceId)
          ? currentDeviceId
          : audioDevices[0]?.deviceId
      )
    }

    const requestPermission = async () => {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: true,
      })
      stream.getTracks().forEach(track => track.stop())
    }

    const initialize = async () => {
      try {
        await requestPermission()
        await loadDevices()
      } catch (error) {
        const permissionDenied =
          error instanceof DOMException && error.name === "NotAllowedError"
        setMicrophoneError(
          permissionDenied
            ? "Microphone permission was denied. Enable microphone access and refresh the page."
            : "No microphone could be accessed. Check your microphone and refresh the page."
        )
      }
    }

    initialize()
    navigator.mediaDevices.addEventListener("devicechange", loadDevices)

    return () => {
      navigator.mediaDevices.removeEventListener("devicechange", loadDevices)
    }
  }, [setSelectedAudioInputDeviceId])

  const selectedAudioInputDeviceName =
    selectedAudioInputDeviceId === "default"
      ? "System Default"
      : audioInputDevices.find(
          device => device.deviceId === selectedAudioInputDeviceId
        )?.label || "Unknown microphone"

  return {
    audioInputDevices,
    selectedAudioInputDeviceId,
    setSelectedAudioInputDeviceId,
    selectedAudioInputDeviceName,
    microphoneError,
  }
}

export default useAudioInputDevices
